import React, { Component } from 'react';
import { observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link } from '@reach/router';
import { keys } from 'lodash';

import { makeRequest, errorResponse } from 'js/services/Network';
import { lightGrey3Color } from 'js/config/style';

import CountDown from 'js/components/core/CountDown';
import LoadingSpinner from '../../LoadingSpinner';

import Container from './Container';
import Title from './Title';
import Content from './Content';
import Display from './Display';
import Label from './Label';
import LogoIcon from './LogoIcon';
import UpdateStatus  from './UpdateStatus';

import { shipmentStatusTranslation } from 'js/stores/pages/AdminDashboardPage/ShipmentStore';

@observer
export default class Shipment extends Component {
  constructor(props) {
    super(props);
    this.load(props.load);
  }

  @observable error = null;
  @observable isLoading = true;
  @observable.ref data = null;

  hasLoadProps = () =>
    keys(this.props.load || {}).reduce((hasLoadProps, prop) => hasLoadProps && !!this.props.load[prop], true);

  @action async load(props) {
    if (this.hasLoadProps()) {
      this.isLoading = true;
      this.error = null;

      try {
        const data = (await makeRequest('admin-shipment-data', null, props)).data;
        runInAction(() => (this.data = data));
      } catch (error) {
        runInAction(() => (this.error = errorResponse(error)));
      }

      runInAction(() => (this.isLoading = false));
    } else {
      this.error = 'Could not load shipment';
      this.isLoading = false;
    }
  }

  forceReload() {
    this.load(this.props.load);
  }

  render() {
    const data = this.data;

    if (!this.hasLoadProps()) {
      return null;
    }

    return (
      <Container mode={this.isLoading ? 'loading' : this.error ? 'error' : 'display'}>
        {this.isLoading ? (
          <LoadingSpinner padding="1rem" />
        ) : this.error ? (
          <>
            <Display>
              <LogoIcon name="truck" color={lightGrey3Color} />
            </Display>
            <Title>{this.props.title || 'Shipment'}</Title>
            <Content>{this.error}</Content>
          </>
        ) : (
          <>
            <Display>
              <LogoIcon name="truck" color={lightGrey3Color} />
            </Display>
            <Title>
              <Link to={`/admin/shipments/shipment/${data.id}`}>{this.props.title || 'Shipment'}</Link>
            </Title>
            <Content>
              <Label>Status</Label>
              <div>
                <div>{shipmentStatusTranslation[data.status] || data.status}</div>

                {data.status === 'processing' && (
                  <UpdateStatus orderId={this.props.load.uid} forceReload={this.forceReload.bind(this)}></UpdateStatus>
                )}

                {data.status === 'pending' && (
                  <CountDown remaining={data.seconds_until_expiration}>
                    {({ status, duration, timeLeft, Remaining }) =>
                      status === 'expired' ? (
                        <>
                          (shipment{' '}
                          <Remaining status={status} title={data.expiration_time}>
                            expired
                          </Remaining>
                          )
                        </>
                      ) : (
                        <>
                          (expires in{' '}
                          <Remaining status={status} title={data.expiration_time}>
                            {status === 'warn' ? timeLeft : duration.humanize()}
                          </Remaining>
                          )
                        </>
                      )
                    }
                  </CountDown>
                )}
              </div>

              <Label>Shipper</Label>
              <div>{data.shipper}</div>

              <Label>Tracking</Label>
              <div>
                <a href={data.tracking_url} target="_blank">
                  {data.tracking_number}
                </a>
              </div>

              <Label>From</Label>
              <div>
                <Link to={`/admin/users/user/${data.from_email}`}>{data.from_name}</Link> ({data.from_summary})
              </div>

              <Label>To</Label>
              <div>
                <Link to={`/admin/users/user/${data.to_email}`}>{data.to_name}</Link> ({data.to_summary})
              </div>
            </Content>
          </>
        )}
      </Container>
    );
  }
}
